/**
 * Typed fetch wrapper for service-* APIs.
 *
 *   - Reads bearer token from `lib/auth/tokenStore` and attaches it as
 *     `Authorization: Bearer <token>`.
 *   - Generates a W3C `traceparent` header per request so service-* logs can
 *     correlate the call. Spec: https://www.w3.org/TR/trace-context/.
 *   - Throws `ApiError` on any non-2xx with a structured body — callers can
 *     pattern-match on status + body to surface 400/422 cleanly.
 *   - 401 path: when a refresh token is in the store, the client transparently
 *     POSTs `${VITE_IDENTITY_BASE_URL}/v1/auth/refresh`, persists the rotated
 *     pair, and retries the original request ONCE. If refresh fails or no
 *     refresh token is stored, it clears the access token (the dev "paste a
 *     JWT" shim user re-pastes; the real login form sends them to
 *     `/auth/login`).
 */
import {
  clearRefreshToken,
  clearToken,
  getRefreshToken,
  getToken,
  setRefreshToken,
  setToken,
} from '../auth/tokenStore';
import { env } from '../env';

export interface ApiErrorShape {
  status: number;
  body: unknown;
  message: string;
}

export class ApiError extends Error implements ApiErrorShape {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, body: unknown, message?: string) {
    super(message ?? `HTTP ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  headers?: Record<string, string>;
  /** When true, suppresses the automatic clearToken-on-401 behaviour AND the
   *  refresh-then-retry flow. Used by the test suite + by callers that want
   *  to keep the token (e.g. the login form itself). */
  suppressAuthReset?: boolean;
  /** Internal flag — set when a request is being retried after a refresh, to
   *  guarantee we never loop. Not part of the public surface. */
  _isRetry?: boolean;
  signal?: AbortSignal;
}

/** Identity-service refresh response shape. The TokenResponseDto returns
 *  `accessToken`, `refreshToken`, `expiresIn`. */
interface RefreshResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

/**
 * Try once to swap the stored refresh token for a fresh access+refresh pair.
 * Returns the new access token on success; null on any failure.
 *
 * Process-singleton in-flight promise so concurrent requests that all 401
 * collapse onto a single `/v1/auth/refresh` call.
 */
let inFlightRefresh: Promise<string | null> | null = null;

async function attemptRefresh(): Promise<string | null> {
  if (inFlightRefresh) return inFlightRefresh;
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  inFlightRefresh = (async () => {
    try {
      const res = await fetch(`${env.identityBaseUrl}/v1/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'content-type': 'application/json',
          accept: 'application/json',
        },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) return null;
      const body = (await res.json()) as RefreshResponse;
      if (!body.accessToken || !body.refreshToken) return null;
      setToken(body.accessToken);
      setRefreshToken(body.refreshToken);
      return body.accessToken;
    } catch {
      return null;
    } finally {
      // Allow the next 401 wave to attempt again.
      inFlightRefresh = null;
    }
  })();

  return inFlightRefresh;
}

/** Per W3C trace-context: `00-<32 hex trace-id>-<16 hex span-id>-01`. */
function generateTraceparent(): string {
  const hex = (length: number): string => {
    let out = '';
    for (let i = 0; i < length; i += 1) {
      out += Math.floor(Math.random() * 16).toString(16);
    }
    return out;
  };
  return `00-${hex(32)}-${hex(16)}-01`;
}

function buildUrl(baseUrl: string, path: string, query?: RequestOptions['query']): string {
  const trimmedBase = baseUrl.replace(/\/$/, '');
  const normalisedPath = path.startsWith('/') ? path : `/${path}`;
  const url = new URL(`${trimmedBase}${normalisedPath}`);
  if (query) {
    for (const [key, raw] of Object.entries(query)) {
      if (raw === undefined || raw === null) {
        continue;
      }
      url.searchParams.set(key, String(raw));
    }
  }
  return url.toString();
}

export async function request<TResponse>(
  baseUrl: string,
  path: string,
  options: RequestOptions = {},
): Promise<TResponse> {
  const { method = 'GET', body, query, headers = {}, suppressAuthReset, signal } = options;

  const finalHeaders: Record<string, string> = {
    accept: 'application/json',
    traceparent: generateTraceparent(),
    ...headers,
  };

  const token = getToken();
  if (token !== null) {
    finalHeaders['authorization'] = `Bearer ${token}`;
  }

  let payload: BodyInit | undefined;
  if (body !== undefined && body !== null) {
    finalHeaders['content-type'] = 'application/json';
    payload = JSON.stringify(body);
  }

  const url = buildUrl(baseUrl, path, query);

  const response = await fetch(url, {
    method,
    headers: finalHeaders,
    body: payload,
    signal,
  });

  // 204 No Content
  if (response.status === 204) {
    return undefined as TResponse;
  }

  let parsedBody: unknown = null;
  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    try {
      parsedBody = await response.json();
    } catch {
      parsedBody = null;
    }
  } else {
    try {
      const text = await response.text();
      parsedBody = text.length > 0 ? text : null;
    } catch {
      parsedBody = null;
    }
  }

  if (!response.ok) {
    if (response.status === 401 && suppressAuthReset !== true) {
      // Refresh-and-retry exactly once. The identity-service `/v1/auth/refresh`
      // rotates the refresh token and mints a new access token. If it
      // succeeds we re-attempt the original request with the fresh bearer
      // and the retry result owns the response. If refresh fails we drop
      // both tokens so the next page render lands on /auth/login.
      if (!options._isRetry) {
        const refreshed = await attemptRefresh();
        if (refreshed !== null) {
          return request<TResponse>(baseUrl, path, { ...options, _isRetry: true });
        }
      }
      clearToken();
      clearRefreshToken();
    }
    throw new ApiError(response.status, parsedBody, `HTTP ${response.status}`);
  }

  return parsedBody as TResponse;
}

export const __test = { generateTraceparent, buildUrl };
